import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useTableSort } from '../../lib/table-sort';
import { useInvestigationBillPatientBills } from '../investigation-bill/investigation-bill.queries';
import { useEmgPatientBills } from './emg.queries';
import { currency, formatEmgDateTime } from './emg.utils';
import type { EmgRegistration } from './types';

interface MergedBillRow {
  id: string;
  billType: 'EMG' | 'Investigation';
  billNumber: string;
  date: string;
  doctorName: string;
  total: number;
  discount: number;
  grandTotal: number;
  paid: number;
  due: number;
  credit: number;
  status: 'due' | 'paid' | 'cancelled';
  link: string;
  isCurrent: boolean;
}

export const EmgAllBillList = ({ row }: { row: EmgRegistration }) => {
  const uhid = row.patientSnapshot.uhid;
  const emgQuery = useEmgPatientBills(uhid);
  const investigationQuery = useInvestigationBillPatientBills(uhid);

  const bills = useMemo<MergedBillRow[]>(() => {
    const emgBills: MergedBillRow[] = (emgQuery.data?.data ?? []).map((bill) => ({
      id: bill.id,
      billType: 'EMG',
      billNumber: bill.billing.billNumber,
      date: bill.registrationDate,
      doctorName: doctorNameOrDash(bill.attendingDoctor?.name),
      total: bill.billing.total,
      discount: bill.billing.totalDiscount,
      grandTotal: bill.billing.grandTotal,
      paid: bill.billing.totalPayment,
      due: bill.billing.dueAmount,
      credit: bill.billing.creditAmount,
      status: bill.billing.billStatus,
      link: `/emg/${bill.id}`,
      isCurrent: bill.id === row.id,
    }));
    const investigationBills: MergedBillRow[] = (investigationQuery.data?.data ?? []).map((bill) => ({
      id: bill.id,
      billType: 'Investigation',
      billNumber: bill.billing.billNumber,
      date: bill.billDate,
      doctorName: doctorNameOrDash(bill.referringDoctor?.name),
      total: bill.billing.total,
      discount: bill.billing.totalDiscount,
      grandTotal: bill.billing.grandTotal,
      paid: bill.billing.totalPayment,
      due: bill.billing.dueAmount,
      credit: bill.billing.creditAmount,
      status: bill.billing.billStatus,
      link: `/investigation-bill/${bill.id}`,
      isCurrent: false,
    }));
    return [...emgBills, ...investigationBills].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [emgQuery.data, investigationQuery.data, row.id]);
  const tableSort = useTableSort(bills, {
    billNo: (bill) => bill.billNumber,
    date: (bill) => bill.date,
    doctor: (bill) => bill.doctorName,
    total: (bill) => bill.total,
    discount: (bill) => bill.discount,
    grandTotal: (bill) => bill.grandTotal,
    paid: (bill) => bill.paid,
    due: (bill) => bill.due,
    credit: (bill) => bill.credit,
    status: (bill) => bill.status,
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their billing history can't be looked up." />;
  if (emgQuery.isLoading || investigationQuery.isLoading) return <LoadingState />;
  if (emgQuery.isError) return <ErrorState title="Unable to load bills" message={emgQuery.error instanceof ApiError ? emgQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void emgQuery.refetch() }} />;
  if (investigationQuery.isError) return <ErrorState title="Unable to load bills" message={investigationQuery.error instanceof ApiError ? investigationQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void investigationQuery.refetch() }} />;

  return (
    <>
      <header className="emg-bill-banner">
        <div className="emg-bill-banner-row"><span>All Bill - {row.patientSnapshot.name}</span></div>
        <p>UHID {uhid} | {bills.length} bill{bills.length === 1 ? '' : 's'}</p>
      </header>
      <section className="emg-bill-subsection">
        {bills.length === 0 ? (
          <p className="emg-bill-empty-note">No bills were found for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="emg-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billNo">Bill No</SortableTh><SortableTh control={tableSort} column="date">Date</SortableTh><SortableTh control={tableSort} column="doctor">Doctor</SortableTh><SortableTh control={tableSort} column="total">Total</SortableTh><SortableTh control={tableSort} column="discount">Discount</SortableTh><SortableTh control={tableSort} column="grandTotal">Grand Total</SortableTh><SortableTh control={tableSort} column="paid">Paid</SortableTh><SortableTh control={tableSort} column="due">Due</SortableTh><SortableTh control={tableSort} column="credit">Credit</SortableTh><SortableTh control={tableSort} column="status">Status</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {tableSort.rows.map((bill, index) => (
                  <tr key={`${bill.billType}-${bill.id}`} className={bill.isCurrent ? 'emg-current-row' : undefined}>
                    <td>{index + 1}</td>
                    <td>{bill.billNumber} <span className={`emg-badge ${bill.billType === 'EMG' ? 'patient-type' : 'rescheduled'}`}>{bill.billType}</span></td>
                    <td>{formatEmgDateTime(bill.date)}</td>
                    <td>{bill.doctorName}</td>
                    <td>{currency(bill.total)}</td>
                    <td>{currency(bill.discount)}</td>
                    <td>{currency(bill.grandTotal)}</td>
                    <td>{currency(bill.paid)}</td>
                    <td>{currency(bill.due)}</td>
                    <td>{currency(bill.credit)}</td>
                    <td><span className={`emg-badge ${bill.status}`}>{bill.status === 'due' ? 'Due' : bill.status === 'cancelled' ? 'Cancelled' : 'Paid'}</span></td>
                    <td><Link className="emg-row-action" to={bill.link}>{bill.isCurrent ? 'Current' : 'View'}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
